import axios from 'axios';

// ⚠️ IMPORTANT: DO NOT CHANGE THIS TO HARDCODED URL IN DEVELOPMENT!
// This MUST use '/api' in development to work with the proxy and avoid CORS errors.
// The proxy (setupProxy.js) forwards /api requests to https://securereservation.org/hotels/api
// Use proxy in development (localhost), full URL in production
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'https://securereservation.org/hotels/api'  // Proxy will forward to https://securereservation.org/hotels/api
  : 'https://securereservation.org/hotels/api';

/**
 * Resolve a hotel_id from the current domain (white-label deployments).
 * Calls getaccommodationgroupdetail?domain=<hostname>. Backend matches the
 * hotel's configured Domain column and returns its accommodation data; if the
 * domain is not configured it returns { error: 'hotel_id_required' } (HTTP 400).
 *
 * @param {string} hostname - bare host, e.g. window.location.hostname (NO https://)
 * @returns {Promise<string|null>} resolved hotel_id, or null if not configured
 */
export const fetchHotelIdByDomain = async (hostname) => {
  if (!hostname) return null;
  try {
    const url = `${API_BASE_URL}/getaccommodationgroupdetail?domain=${encodeURIComponent(hostname)}`;
    const response = await axios.get(url, { timeout: 30000, responseType: 'json' });
    let data = response.data;
    // Defensive: some servers send JSON with a non-json content-type → axios may
    // leave it as a string. Parse it so the field lookup works.
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch (_) { /* leave as-is */ }
    }
    const id = data?.hotelmapping?.AccommodationId
      || data?.accommodationdetailsdb?.[0]?.AccommodationId
      || null;
    return id ? String(id) : null;
  } catch (e) {
    // 400 hotel_id_required / network error → domain not configured
    return null;
  }
};

/**
 * Fetch accommodation details from the API
 *
 * @param {string} hotelId - Hotel accommodation ID (from URL parameter)
 * @returns {Promise<Object>} Accommodation details object
 */
export const fetchAccommodationDetail = async (hotelId) => {
  try {
    if (!hotelId) {
      throw new Error('Hotel ID is required');
    }

    const url = `${API_BASE_URL}/getaccommodationdetail?hotel_id=${hotelId}`;
    
    
    let response;
    try {
      response = await axios.get(url, {
        timeout: 30000,
        responseType: 'json',
      });
    } catch (networkError) {
      if (networkError.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please check your connection and try again.');
      }
      if (networkError.response) {
        throw new Error(`Server error: ${networkError.response.status} ${networkError.response.statusText}`);
      }
      if (networkError.request) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw networkError;
    }

    const accommodationData = response.data;
    // Extract accommodation details from response
    if (accommodationData.Success && accommodationData.Success.Result) {
      const result = accommodationData.Success.Result;
      return {
        accommodationId: result.AccommodationId || hotelId,
        accommodationName: result.AccommodationName || '',
        accommodationTypeName: result.AccommodationTypeName || '',
        address: result.Address || '',
        logoURL: result.LogoURL || '',
        contactNo: result.ContactNo || '',
        emailId: result.EmailId || '',
        website: result.Website || '',
        generalDescription: result.GeneralDescription || '',
        currencyName: result.CurrencyName || '',
        currencyCode: result.CurrencyCode || '',
        rating: result.Rating || '',
        userRating: result.UserRating || '',
        checkin: result.checkin || { from: '14:00', to: [] },
        checkout: result.checkout || { from: '11:00', to: [] },
        hotelExtras: result.hotelExtras || {},
        hotelImages: result.hotelImages || {},
        location: result.Location || {},
        ...result // Preserve all other fields
      };
    }

    throw new Error('Invalid accommodation data format');
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch accommodation group details from the API
 * 
 * @param {string} hotelId - Hotel accommodation ID (from URL parameter)
 * @returns {Promise<Object>} Accommodation group details object
 */
export const fetchAccommodationGroupDetail = async (hotelId) => {
  try {
    if (!hotelId) {
      throw new Error('Hotel ID is required');
    }

    // Always send the current domain too — backend checks domain FIRST and only
    // falls back to hotel_id when the domain column is empty / not matched.
    const hostname = (typeof window !== 'undefined' && window.location?.hostname) || '';
    const url = `${API_BASE_URL}/getaccommodationgroupdetail?domain=${encodeURIComponent(hostname)}&hotel_id=${hotelId}`;


    let response;
    try {
      response = await axios.get(url, {
        timeout: 30000,
        responseType: 'json',
      });
    } catch (networkError) {
      if (networkError.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please check your connection and try again.');
      }
      if (networkError.response) {
        throw new Error(`Server error: ${networkError.response.status} ${networkError.response.statusText}`);
      }
      if (networkError.request) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw networkError;
    }

    const groupData = response.data;

    // Extract accommodation group details from response
    // Structure: { accommodationdetailsdb: Array(1), hotelmapping: {...} }
    // accommodationdetailsdb[0] contains: { AccessKey: "...", AccommodationId: 2011, ... }
    if (groupData && (groupData.accommodationdetailsdb || groupData.hotelmapping)) {
      return {
        // Preserve original accommodationdetailsdb array
        accommodationdetailsdb: groupData.accommodationdetailsdb || [],
        // Also map to accommodationDetails for backward compatibility
        accommodationDetails: groupData.accommodationdetailsdb || [],
        hotelMapping: groupData.hotelmapping || {},
        groupId: groupData.hotelmapping?.GroupId || null,
        accommodationId: groupData.hotelmapping?.AccommodationId || hotelId,
        status: groupData.hotelmapping?.Status || null,
        isShowList: groupData.hotelmapping?.Is_show_list || false,
        isGroupCss: groupData.hotelmapping?.IsGroupCss || false,
        ...groupData // Preserve all other fields from API response
      };
    }

    throw new Error('Invalid accommodation group data format');
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch hotel list by group from the API
 * 
 * @param {number} groupId - Group ID
 * @param {number} langId - Language ID (default: 1)
 * @returns {Promise<Array>} Array of accommodation objects
 */
export const fetchHotelListByGroup = async (groupId, langId = 1) => {
  try {
    if (!groupId) {
      throw new Error('Group ID is required');
    }

    const url = `${API_BASE_URL}/hotellistbygroup?lang=${langId}&group_id=${groupId}`;
    
    
    let response;
    try {
      response = await axios.get(url, {
        timeout: 30000,
        responseType: 'json',
      });
    } catch (networkError) {
      if (networkError.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please check your connection and try again.');
      }
      if (networkError.response) {
        throw new Error(`Server error: ${networkError.response.status} ${networkError.response.statusText}`);
      }
      if (networkError.request) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw networkError;
    }

    const data = response.data;

    // Extract accommodations from hotelGrouping object
    if (data && data.hotelGrouping) {
      const accommodations = [];
      
      // Iterate through hotelGrouping object
      Object.keys(data.hotelGrouping).forEach((key) => {
        const groupItem = data.hotelGrouping[key];
        if (groupItem && groupItem.accommodations) {
          const acc = groupItem.accommodations;
          accommodations.push({
            accommodationId: acc.AccommodationId || acc.accommodationId,
            accommodationName: acc.AccommodationName || acc.accommodationName,
            address: acc.Address || acc.address,
            cityId: acc.CityId || acc.cityId,
            countryId: acc.CountryId || acc.countryId,
            logoUrl: acc.LogoUrl || acc.logoUrl,
            starRating: acc.StarRating || acc.starRating,
            websiteUrl: acc.WebsiteUrl || acc.websiteUrl,
            status: groupItem.Status || groupItem.status,
            isShowList: groupItem.Is_show_list || groupItem.isShowList,
            groupId: groupItem.GroupId || groupItem.groupId,
            ...acc // Preserve all other fields
          });
        }
      });
      
      return accommodations;
    }

    throw new Error('Invalid hotel list data format');
  } catch (error) {
    throw error;
  }
};

